/**
 * SidebarProfileFooter · v2.7.65+E140
 * ═════════════════════════════════════════════════════════════════════════
 * Pinned bottom slot of the Admin Console sidebar. Shows the current
 * operator's avatar + presence dot, their Employee # and Company Position,
 * and opens an in-place dropdown so they can flip their presence between
 * the 7 states defined in the redesign spec.
 *
 * Data flow
 * ─────────
 *   GET  /api/admin/me/profile         → hydrate avatar / emp# / position
 *   PATCH /api/admin/me/presence       → persist status change (+ WS push)
 *   WS   /ws/dispatch → event: user.presence_changed
 *                       (multi-tab / cross-console live sync)
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, ChevronDown, Pencil, X, Check } from "lucide-react";
import { BUILD_VERSION, BUILD_DATE, getBuildEnv } from "@/lib/buildInfo";
import { ProfilePhotoUploader, SavedTick } from "@/components/admin/ProfilePhotoUploader";

// v2.7.65+E140 · Spec-mandated presence set + colours.
export const PRESENCE_OPTIONS = [
  { key: "online",   label: "Online",    color: "#22C55E" },
  { key: "away",     label: "Away",      color: "#EAB308" },
  { key: "on_break", label: "On Break",  color: "#EAB308" },
  { key: "meeting",  label: "Meeting",   color: "#EF4444" },
  { key: "on_call",  label: "On Call",   color: "#EF4444" },
  { key: "dnd",      label: "DND",       color: "#EF4444" },
  { key: "vacation", label: "Vacation",  color: "#3B82F6" },
  { key: "offline",  label: "Offline",   color: "#9CA3AF" },
];

const PRESENCE_BY_KEY = Object.fromEntries(PRESENCE_OPTIONS.map((p) => [p.key, p]));

const API = process.env.REACT_APP_BACKEND_URL;

function initialsFrom(profile) {
  const fn = (profile?.first_name || "").trim();
  const ln = (profile?.last_name || "").trim();
  if (fn || ln) return `${fn.charAt(0)}${ln.charAt(0)}`.toUpperCase() || "?";
  const u = profile?.username || "";
  return u.charAt(0).toUpperCase() || "?";
}

// v2.8+E349 · any profile change (photo, Emp ID, position) → cards re-read it.
export const PROFILE_UPDATED_EVENT = "rtg:profile-updated";
export const announceProfileUpdated = () => {
  try { window.dispatchEvent(new CustomEvent(PROFILE_UPDATED_EVENT)); } catch { /* no-op */ }
};

function EditProfileMetaModal({ open, onClose, profile, onSaved }) {
  const [empNo, setEmpNo] = useState(profile?.employee_number || "");
  const [position, setPosition] = useState(profile?.company_position || "");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState({});
  useEffect(() => {
    if (open) {
      setEmpNo(profile?.employee_number || "");
      setPosition(profile?.company_position || "");
    }
  }, [open, profile]);
  useEffect(() => { if (!open) setSaved({}); }, [open]);
  if (!open) return null;
  const persist = async (body) => {
    const token = localStorage.getItem("rtg_admin_token");
    const res = await fetch(`${API}/api/admin/me/profile`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error((await res.json())?.detail || "Save failed");
    const data = await res.json();
    onSaved?.(data);
    announceProfileUpdated();
    return data;
  };
  // Auto-save a field as soon as the operator leaves it.
  const autoSave = async (field, value, current) => {
    if ((value || "").trim() === (current || "").trim()) return;
    try {
      await persist({ [field]: value });
      setSaved((s) => ({ ...s, [field]: true }));
      toast.success("Profile saved");
    } catch (e) { toast.error(e.message); }
  };
  const save = async () => {
    setBusy(true);
    try {
      await persist({ employee_number: empNo, company_position: position });
      toast.success("Profile updated");
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally { setBusy(false); }
  };
  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm grid place-items-center p-4"
      data-testid="sidebar-profile-meta-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-white/10 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest">Edit profile</div>
          <button onClick={onClose} className="text-slate-500 dark:text-white/50 hover:text-slate-900 dark:hover:text-white" aria-label="Close">
            <X className="h-4 w-4"/>
          </button>
        </div>
        <div className="mb-4"><ProfilePhotoUploader testIdPrefix="sidebar-profile-photo"/></div>
        <label className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 dark:text-white/50 font-bold mb-1">
          Employee # <SavedTick show={saved.employee_number} testId="sidebar-profile-empno-saved"/>
        </label>
        <input
          value={empNo}
          onChange={(e) => setEmpNo(e.target.value)}
          onBlur={() => autoSave("employee_number", empNo, profile?.employee_number)}
          placeholder="e.g. 104"
          data-testid="sidebar-profile-empno-input"
          className="w-full bg-white dark:bg-zinc-950 border border-zinc-300 dark:border-white/10 rounded-md px-3 py-2 text-sm text-slate-900 dark:text-white mb-3 focus:outline-none focus:border-amber-400/60"
        />
        <label className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 dark:text-white/50 font-bold mb-1">
          Company Position <SavedTick show={saved.company_position} testId="sidebar-profile-position-saved"/>
        </label>
        <input
          value={position}
          onChange={(e) => setPosition(e.target.value)}
          onBlur={() => autoSave("company_position", position, profile?.company_position)}
          placeholder="e.g. Chief Operating Officer"
          data-testid="sidebar-profile-position-input"
          className="w-full bg-white dark:bg-zinc-950 border border-zinc-300 dark:border-white/10 rounded-md px-3 py-2 text-sm text-slate-900 dark:text-white mb-4 focus:outline-none focus:border-amber-400/60"
        />
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-sm text-slate-600 dark:text-white/70 hover:text-slate-900 dark:hover:text-white"
            data-testid="sidebar-profile-meta-cancel"
          >Cancel</button>
          <button
            onClick={save}
            disabled={busy}
            data-testid="sidebar-profile-meta-save"
            className="px-4 py-1.5 rounded-md text-sm bg-amber-500 hover:bg-amber-400 text-black font-bold flex items-center gap-1.5 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <Check className="h-3.5 w-3.5"/>}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SidebarProfileFooter({ micro = false }) {
  const [profile, setProfile] = useState(null);
  const [presence, setPresence] = useState("online");
  const [note, setNote] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef(null);

  const reload = useCallback(async () => {
    const token = localStorage.getItem("rtg_admin_token");
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/admin/me/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setProfile(data);
      setPresence(data.presence || "online");
      setNote(data.presence_note || "");
    } catch { /* network blip */ }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // v2.8+E349 · refresh the card the moment any editor saves a change.
  useEffect(() => {
    window.addEventListener(PROFILE_UPDATED_EVENT, reload);
    return () => window.removeEventListener(PROFILE_UPDATED_EVENT, reload);
  }, [reload]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // WebSocket live sync — listen for user.presence_changed on the shared
  // dispatch stream. When the event matches our own username, refresh so
  // multi-tab sessions stay in sync.
  useEffect(() => {
    if (!API) return;
    let ws;
    let closed = false;
    const wsUrl = API.replace(/^http/, "ws") + "/ws/dispatch";
    try {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg?.event === "user.presence_changed" && msg.username === profile?.username) {
            setPresence(msg.presence);
            setNote(msg.note || "");
          }
        } catch { /* ignore malformed */ }
      };
      ws.onerror = () => { /* silent — REST is source of truth */ };
    } catch { /* WebSocket unsupported / blocked */ }
    return () => { closed = true; try { ws?.close(); } catch { /* no-op */ } void closed; };
  }, [profile?.username]);

  const setPresenceRemote = async (key) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("rtg_admin_token");
      const res = await fetch(`${API}/api/admin/me/presence`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: key }),
      });
      if (!res.ok) throw new Error((await res.json())?.detail || "Update failed");
      const data = await res.json();
      setPresence(data.presence || key);
      setNote(data.presence_note || "");
      toast.success(`Status set to ${PRESENCE_BY_KEY[key]?.label || key}`);
      setOpen(false);
    } catch (e) {
      toast.error(e.message);
    } finally { setSaving(false); }
  };

  const meta = PRESENCE_BY_KEY[presence] || PRESENCE_BY_KEY.offline;
  const empLabel = profile?.employee_number ? `Emp ID: #${profile.employee_number}` : "Emp ID: —";
  const posLabel = profile?.company_position || (profile?.canonical_role ? profile.canonical_role : "Set position");
  const displayName = (profile?.full_name?.trim())
    || [profile?.first_name, profile?.last_name].filter(Boolean).join(" ")
    || profile?.username
    || "Signed in";

  return (
    <div className={`${micro ? "mt-2 pt-2" : "mt-4 pt-3"} border-t border-zinc-200 dark:border-white/10`} ref={wrapRef} data-testid="sidebar-profile-footer" data-micro={micro ? "true" : "false"}>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="sidebar-profile-toggle"
          title={micro ? `${displayName} · ${meta.label}` : undefined}
          className={`w-full flex items-center ${micro ? "justify-center px-1 py-1" : "gap-3 px-2 py-2"} rounded-lg hover:bg-black/[0.04] dark:hover:bg-white/[0.08] transition-colors text-left`}
        >
          <div className="relative flex-shrink-0">
            {profile?.profile_image_url ? (
              <img
                src={profile.profile_image_url.startsWith("http") ? profile.profile_image_url : `${API}${profile.profile_image_url}`}
                alt="avatar"
                className={`${micro ? "h-9 w-9" : "h-10 w-10"} rounded-full object-cover ring-2 ring-zinc-200 dark:ring-white/10`}
              />
            ) : (
              <div className={`${micro ? "h-9 w-9 text-xs" : "h-10 w-10 text-sm"} rounded-full grid place-items-center bg-amber-500/20 text-amber-700 dark:text-amber-200 font-black ring-2 ring-zinc-200 dark:ring-white/10`}>
                {initialsFrom(profile)}
              </div>
            )}
            {/* Presence dot */}
            <span
              className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full ring-2 ring-white dark:ring-slate-950"
              style={{ backgroundColor: meta.color }}
              title={meta.label}
              data-testid="sidebar-profile-presence-dot"
              data-presence={presence}
            />
          </div>
          {!micro && (
            <>
              <div className="min-w-0 flex-1">
                <div className="text-xs text-slate-900 dark:text-white truncate font-bold flex items-center gap-1.5" data-testid="sidebar-profile-name">
                  <span className="truncate">{displayName}</span>
                  {/* v2.7.65+E162 · Platform Owner badge — only rendered
                      when the API `profile.is_platform_owner` flag is
                      true. Two-key locked server-side (email +
                      is_platform_owner) so the flag alone can never be
                      spoofed via localStorage or a stale JWT claim. */}
                  {profile?.is_platform_owner && (
                    <span
                      className="text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-400/25 text-amber-700 dark:text-amber-200 border border-amber-500/60 flex-shrink-0"
                      data-testid="sidebar-profile-platform-owner-badge"
                      title="Platform Owner · final authority over Buller Taxi platform"
                    >
                      Platform Owner
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-slate-600 dark:text-white/60 truncate" data-testid="sidebar-profile-emp">
                  {empLabel}
                </div>
                <div className="text-[10px] text-slate-500 dark:text-white/50 truncate" data-testid="sidebar-profile-position">
                  {posLabel}
                </div>
              </div>
              {/* v2.7.65+E191 · Sidebar self-edit shortcut. A prominent
                  pencil icon skips straight to the right editor:
                    · DB-backed staff (drivers/supervisors/dispatchers/etc.)
                      → dispatch a global `rtg:open-dossier` event so
                        the AdminDashboardPage-level DriverDossierDialog
                        pops open on top of whatever tab is active. No
                        forced tab switch, no full page reload.
                    · Env-admin (no driver_id on profile) → open the
                      lightweight EditProfileMetaModal in place so they
                      can still set Emp # and Position.
                  Click stops propagation so the parent button doesn't
                  swap the presence dropdown open. */}
              {/* NOTE: rendered as a role=button <span> — not a <button> —
                  because it lives INSIDE the profile-toggle button and
                  nested buttons are invalid DOM (React logs a hydration
                  error and React 19 hard-fails). */}
              <span
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.currentTarget.click();
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  const did = profile?.driver_id || profile?.id;
                  if (did && profile?.kind !== "env_admin") {
                    try {
                      window.dispatchEvent(new CustomEvent("rtg:open-dossier", { detail: { driverId: did, self: true } }));
                    } catch {
                      // Fallback: full navigation with query param.
                      window.location.assign(`/admin?dossier=${encodeURIComponent(did)}`);
                    }
                  } else {
                    setEditing(true);
                    setOpen(false);
                  }
                }}
                title="Edit my Emp ID and Position"
                aria-label="Edit my Emp ID and Position"
                className="flex-shrink-0 p-1.5 rounded text-slate-500 dark:text-white/50 hover:text-amber-600 dark:hover:text-amber-300 hover:bg-black/[0.05] dark:hover:bg-white/[0.08] transition-colors cursor-pointer"
                data-testid="sidebar-profile-edit-pencil"
              >
                <Pencil className="h-3.5 w-3.5"/>
              </span>
              <ChevronDown
                className={`h-3.5 w-3.5 text-slate-400 dark:text-white/40 transition-transform ${open ? "rotate-180" : ""}`}
              />
            </>
          )}
        </button>

        {open && (
          <div
            className="absolute bottom-full mb-2 left-0 right-0 rounded-lg bg-white dark:bg-slate-950 border border-zinc-200 dark:border-white/15 shadow-2xl overflow-hidden z-50"
            data-testid="sidebar-profile-dropdown"
          >
            <div className="px-3 py-2 border-b border-zinc-200 dark:border-white/10 flex items-center justify-between">
              <div className="text-[9px] uppercase tracking-widest text-slate-500 dark:text-white/50 font-bold">Set status</div>
              <button
                onClick={(e) => { e.stopPropagation(); setEditing(true); setOpen(false); }}
                className="text-slate-500 dark:text-white/50 hover:text-amber-600 dark:hover:text-amber-300 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1"
                data-testid="sidebar-profile-edit-meta"
              >
                <Pencil className="h-3 w-3"/>Edit
              </button>
            </div>
            <ul className="py-1 max-h-64 overflow-y-auto">
              {PRESENCE_OPTIONS.map((opt) => {
                const active = presence === opt.key;
                return (
                  <li key={opt.key}>
                    <button
                      onClick={() => setPresenceRemote(opt.key)}
                      disabled={saving}
                      data-testid={`sidebar-profile-status-${opt.key}`}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs transition-colors ${
                        active
                          ? "bg-black/[0.06] dark:bg-white/[0.12] text-slate-900 dark:text-white font-bold"
                          : "text-slate-700 dark:text-white/85 hover:bg-black/[0.04] dark:hover:bg-white/[0.08]"
                      }`}
                    >
                      <span
                        className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: opt.color }}
                      />
                      <span className="flex-1 text-left">{opt.label}</span>
                      {active && <Check className="h-3 w-3 text-amber-600 dark:text-amber-300"/>}
                    </button>
                  </li>
                );
              })}
            </ul>
            {note && (
              <div className="px-3 py-2 border-t border-zinc-200 dark:border-white/10 text-[10px] text-slate-600 dark:text-white/60 italic truncate">
                {note}
              </div>
            )}
          </div>
        )}
      </div>

      <EditProfileMetaModal
        open={editing}
        onClose={() => setEditing(false)}
        profile={profile}
        onSaved={(fresh) => setProfile(fresh)}
      />

      {/* v2.7.65+E224 · Build version + environment stamp. Confirms at a glance
          whether the live site (bullertaxi.com) is running the latest fixes. */}
      <BuildVersionStamp micro={micro} />
    </div>
  );
}

function BuildVersionStamp({ micro }) {
  const env = getBuildEnv();
  const prod = env.key === "production";
  const title = `${env.label} · build v${BUILD_VERSION} · ${BUILD_DATE}`;
  if (micro) {
    return (
      <div className="mt-2 flex justify-center" data-testid="build-version-badge" data-build-env={env.key} data-build-version={BUILD_VERSION} title={title}>
        <span className={`h-1.5 w-1.5 rounded-full ${prod ? "bg-emerald-400" : "bg-amber-400"}`} />
      </div>
    );
  }
  return (
    <div
      className="mt-2 px-1 flex items-center justify-between gap-1"
      data-testid="build-version-badge"
      data-build-env={env.key}
      data-build-version={BUILD_VERSION}
      title={title}
    >
      <span
        className={`text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded border flex-shrink-0 ${
          prod
            ? "bg-emerald-400/15 text-emerald-600 dark:text-emerald-300 border-emerald-500/50"
            : "bg-amber-400/15 text-amber-600 dark:text-amber-300 border-amber-500/50"
        }`}
        data-testid="build-version-env"
      >
        {env.label}
      </span>
      <span className="text-[9px] text-slate-400 dark:text-white/40 font-mono truncate" data-testid="build-version-tag">
        v{BUILD_VERSION}
      </span>
    </div>
  );
}
